// ProductCard.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaRegHeart, FaHeart, FaExpand } from 'react-icons/fa';
import { wishlistService } from '../../services/wishlistService';

const ProductCard = ({ product }) => {
  const {
    _id,
    name,
    price,
    salePrice,
    images,
    dimensions,
    modelUrl,
    isOnSale,
    isNewArrival,
    isBestSeller
  } = product;

  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [isAddingToWishlist, setIsAddingToWishlist] = useState(false);
  const [imageHovered, setImageHovered] = useState(false);

  // Check if product is in wishlist when component mounts
  useEffect(() => {
    const checkWishlistStatus = async () => {
      if (isAuthenticated) {
        try {
          const status = await wishlistService.isInWishlist(_id);
          setIsInWishlist(status);
        } catch (err) {
          console.error('Error checking wishlist status:', err);
        }
      }
    };
    checkWishlistStatus();
  }, [_id, isAuthenticated]);

  // Handle wishlist toggle
  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      sessionStorage.setItem('redirectAfterLogin', `/product/${_id}`);
      navigate('/login');
      return;
    }
    
    try {
      setIsAddingToWishlist(true);
      
      if (isInWishlist) {
        await wishlistService.removeFromWishlist(_id);
        setIsInWishlist(false);
      } else {
        await wishlistService.addToWishlist(_id);
        setIsInWishlist(true);
      }
    } catch (err) {
      console.error('Wishlist operation failed:', err);
    } finally {
      setIsAddingToWishlist(false);
    }
  };

  return (
    <div className="group relative" id={`product-${_id}`}>
      {/* PRODUCT IMAGE CONTAINER */}
      <div 
        className="relative bg-gray-100 overflow-hidden aspect-square"
        style={{ backgroundColor: imageHovered ? '#e6f7f5' : '#f3f4f6' }}
        onMouseEnter={(e) => {
          e.stopPropagation();
          setImageHovered(true);
        }}
        onMouseLeave={(e) => {
          e.stopPropagation();
          setImageHovered(false);
        }}
      >
        <Link to={`/product/${_id}`}>
          {/* Main Image */}
          <img
            src={images[0]}
            alt={name}
            className="w-full h-full object-cover object-center transition-opacity duration-500"
            style={{ opacity: imageHovered && images.length > 1 ? 0 : 1 }}
          />
          
          {/* Second Image on hover */}
          {images.length > 1 && (
            <img
              src={images[1]}
              alt={`${name} - alternate view`}
              className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-500"
              style={{ opacity: imageHovered ? 1 : 0 }}
            />
          )}
        </Link>

        {/* Status Label */}
        {isOnSale && (
          <div className="absolute top-3 left-0 px-3 py-1 bg-white text-red-600 text-xs tracking-wider font-light uppercase">
            Sale
          </div>
        )}

        {/* Wishlist Button */}
        <button 
          onClick={handleWishlistToggle}
          disabled={isAddingToWishlist}
          className="absolute top-3 right-3 p-2 rounded-full bg-white shadow-sm hover:shadow transition-shadow"
        >
          {isAddingToWishlist ? (
            <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full"></div>
          ) : isInWishlist ? (
            <FaHeart className="text-red-500 h-4 w-4" />
          ) : (
            <FaRegHeart className="text-gray-600 h-4 w-4" />
          )}
        </button>

        {/* Quick View Button - ONLY VISIBLE ON HOVER */}
        {imageHovered && (
          <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-95 text-center py-2">
            <Link 
              to={`/product/${_id}`} 
              className="flex items-center justify-center text-gray-900"
            >
              <FaExpand className="h-3 w-3 mr-2" />
              <span className="text-xs uppercase tracking-wider">Quick View</span>
            </Link>
          </div>
        )}
      </div>

      {/* PRODUCT INFO - WITH PROPER SPACING */}
      <div className="text-center mt-4 pt-2">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest">
          <Link to={`/product/${_id}`} className="hover:text-teal-800 transition-colors">
            {name}
          </Link>
        </h3>
        
        <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">
          {dimensions}
        </p>
        {/* <div className="mt-1 font-light">
          {isOnSale ? (
            <div className="flex items-center justify-center">
              <span className="text-red-600">${salePrice}</span>
              <span className="ml-2 text-xs text-gray-400 line-through">${price}</span>
            </div>
          ) : (
            <span className="text-gray-900">${price}</span>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default ProductCard;